"use client";

import React from "react";
import {
  LegalPageShell,
  LegalHero,
  fraunces,
  ACCENT,
} from "@/app/components/legal/LegalPageShell";
import { useLegalDocScroll } from "@/app/hooks/useLegalDocScroll";
import { PrivacyPolicyContent } from "./content";
import { renderLegalContent, slugify } from "@/src/shared/legal/legalContentRender";
import { MobileLegalSectionNav } from "@/app/components/legal/MobileLegalSectionNav";

const RGB = ACCENT.privacy;

export default function PrivacyPolicy() {
  const { scrollRef, activeIndex, progress } = useLegalDocScroll();
  const sections = PrivacyPolicyContent.sections;

  return (
    <LegalPageShell
      ref={scrollRef}
      variant="privacy"
      progress={progress}
      hero={<LegalHero eyebrow="Legal" title="Privacy Policy" meta={`Last updated ${PrivacyPolicyContent.lastUpdated}`} />}
    >
      <main className="mx-auto max-w-[960px] px-6 pb-20 md:px-8">
        <MobileLegalSectionNav
          activeIndex={activeIndex}
          sections={sections.map((section) => ({ id: `s-${slugify(section.title)}`, title: section.title }))}
        />
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[180px_1fr] lg:gap-20">
          {/* TOC */}
          <aside className="hidden lg:block">
            <nav
              className="legal-toc sticky top-24 max-h-[calc(100vh-8rem)] space-y-1 overflow-y-auto pr-2"
              aria-label="Sections"
            >
              {sections.map((section, i) => {
                const id = `s-${slugify(section.title)}`;
                const isActive = activeIndex === i;
                return (
                  <a
                    key={id}
                    href={`#${id}`}
                    aria-current={isActive ? "location" : undefined}
                    className={`flex min-h-11 items-center rounded-sm py-2 text-[12.5px] leading-snug transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white motion-reduce:transition-none ${
                      isActive ? "text-white/90" : "text-white/45 hover:text-white/70"
                    }`}
                  >
                    {section.title}
                  </a>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <article>
            {sections.map((section, i) => {
              const id = `s-${slugify(section.title)}`;
              return (
                <section
                  key={id}
                  id={id}
                  data-section=""
                  className="scroll-mt-28 pb-12 pt-12 first:pt-0"
                  style={{
                    animation: `legalFadeIn 0.5s ease-out ${0.25 + i * 0.03}s both`,
                    borderBottom: i < sections.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                  }}
                >
                  <div className="mb-6 flex items-baseline gap-4">
                    <span
                      className={`${fraunces.className} text-[1.3rem] font-light leading-none text-white/10 select-none`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className={`${fraunces.className} text-[1.3rem] font-normal leading-snug text-white/95 md:text-[1.5rem]`}>
                      {section.title}
                    </h2>
                  </div>
                  <div className="pl-0 md:pl-12">
                    {renderLegalContent(section.content, RGB)}
                  </div>
                </section>
              );
            })}
          </article>
        </div>
      </main>
    </LegalPageShell>
  );
}
